import type { Bytes } from '../model/binary.ts';
import { readImageHeader, type ImageHeader } from './imageHeader.ts';
import { DEFAULT_LIMITS, type AllowedImageMime, type Limits } from './limits.ts';

/**
 * Byte-level checks for embedded media.
 *
 * The rule is that a declared MIME type from an imported file is a claim, not a fact. Everything
 * here exists to verify the claim against the actual bytes before a URL is ever created for them.
 */

export type SniffedType = AllowedImageMime | 'image/svg+xml' | 'unknown';

const BASE64_PATTERN = new RegExp('^[A-Za-z0-9+/]*={0,2}$');

export type Base64Result =
  | { readonly ok: true; readonly bytes: Bytes }
  | { readonly ok: false; readonly reason: 'invalid-characters' | 'invalid-length' | 'decode-failed' };

/**
 * Decodes base64 without throwing.
 *
 * The character and length checks run first because `atob` throws on malformed input, and an
 * exception crossing the worker boundary is a worse diagnostic than a typed failure.
 */
export function decodeBase64(value: string): Base64Result {
  const compact = value.replace(/\s+/g, '');
  if (!BASE64_PATTERN.test(compact)) {
    return { ok: false, reason: 'invalid-characters' };
  }
  if (compact.length % 4 !== 0) {
    return { ok: false, reason: 'invalid-length' };
  }

  try {
    const binary = atob(compact);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return { ok: true, bytes };
  } catch {
    return { ok: false, reason: 'decode-failed' };
  }
}

function startsWith(bytes: Bytes, signature: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + signature.length) {
    return false;
  }
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG = [0xff, 0xd8, 0xff];
const GIF87 = [0x47, 0x49, 0x46, 0x38, 0x37, 0x61];
const GIF89 = [0x47, 0x49, 0x46, 0x38, 0x39, 0x61];
const RIFF = [0x52, 0x49, 0x46, 0x46];
const WEBP = [0x57, 0x45, 0x42, 0x50];

/**
 * Bytes back to base64, for writing an embedded image into a corrected exam.
 *
 * Chunked because `String.fromCharCode` is applied to the whole array at once by the obvious
 * implementation, and a two-megabyte image is enough arguments to overflow the call stack.
 */
export function encodeBase64(bytes: Bytes): string {
  const CHUNK = 0x80_00;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

/**
 * Identifies an image from its magic bytes, ignoring whatever the file claimed.
 *
 * SVG gets its own return value rather than falling into `unknown`, because "this is an SVG and we
 * refuse SVG" is a message a user can act on, while "unrecognised image" is not. SVG is refused
 * because it is an XML document that can carry script — an image format that executes.
 */
export function sniffImageType(bytes: Bytes): SniffedType {
  if (startsWith(bytes, PNG)) {
    return 'image/png';
  }
  if (startsWith(bytes, JPEG)) {
    return 'image/jpeg';
  }
  if (startsWith(bytes, GIF87) || startsWith(bytes, GIF89)) {
    return 'image/gif';
  }
  if (startsWith(bytes, RIFF) && startsWith(bytes, WEBP, 8)) {
    return 'image/webp';
  }
  if (looksLikeSvg(bytes)) {
    return 'image/svg+xml';
  }
  return 'unknown';
}

/**
 * SVG has no magic number, so this sniffs the leading text for an XML or SVG opening tag,
 * tolerating a BOM and leading whitespace, comments and doctypes.
 */
function looksLikeSvg(bytes: Bytes): boolean {
  const head = new TextDecoder('utf-8', { fatal: false }).decode(bytes.subarray(0, 1024));
  // TextDecoder already strips a leading BOM, so only whitespace can precede the tag.
  return new RegExp('^\\s*(<\\?xml[\\s>]|<!--|<!DOCTYPE\\s+svg|<svg[\\s>])', 'i').test(head);
}

export type EmbeddedImageRejection =
  | 'too-large'
  | 'declared-type-not-allowed'
  | 'type-mismatch'
  | 'unrecognised'
  | 'svg-not-allowed'
  | 'dimensions-too-large'
  | 'header-unreadable';

export type EmbeddedImageCheck =
  | {
      readonly ok: true;
      readonly mimeType: AllowedImageMime;
      readonly byteLength: number;
      readonly header: ImageHeader;
    }
  | {
      readonly ok: false;
      readonly reason: EmbeddedImageRejection;
      readonly declared: string;
      readonly detected: SniffedType;
    };

/** Verifies decoded image bytes against the type the file declared and against the size budget. */
export function checkEmbeddedImage(
  declaredMimeType: string,
  bytes: Bytes,
  limits: Limits = DEFAULT_LIMITS,
): EmbeddedImageCheck {
  const detected = sniffImageType(bytes);

  if (bytes.byteLength > limits.maxEmbeddedAssetBytes) {
    return { ok: false, reason: 'too-large', declared: declaredMimeType, detected };
  }
  if (detected === 'image/svg+xml') {
    return { ok: false, reason: 'svg-not-allowed', declared: declaredMimeType, detected };
  }
  if (!isAllowedImageMime(declaredMimeType, limits)) {
    return { ok: false, reason: 'declared-type-not-allowed', declared: declaredMimeType, detected };
  }
  if (detected === 'unknown') {
    return { ok: false, reason: 'unrecognised', declared: declaredMimeType, detected };
  }
  if (detected !== declaredMimeType) {
    return { ok: false, reason: 'type-mismatch', declared: declaredMimeType, detected };
  }

  /*
   * The last check, and the one the byte budget above cannot make.
   *
   * Everything so far has bounded the *compressed* file, and compression is exactly what an
   * attacker controls. What matters is how much the browser will be asked to hold when it draws
   * this, which the header states and which costs a few dozen bytes to read.
   *
   * An unreadable header is refused rather than waved through. This is a security check on bytes
   * somebody sent, so it fails closed: a header these parsers cannot read is either a format they
   * do not know or a file that has been damaged, and neither is something to hand to a decoder.
   */
  const header = readImageHeader(detected, bytes);
  if (header === null) {
    return { ok: false, reason: 'header-unreadable', declared: declaredMimeType, detected };
  }
  if (
    header.width > limits.maxImageDimension ||
    header.height > limits.maxImageDimension ||
    header.width * header.height * header.frames > limits.maxImagePixels
  ) {
    return { ok: false, reason: 'dimensions-too-large', declared: declaredMimeType, detected };
  }

  return { ok: true, mimeType: detected, byteLength: bytes.byteLength, header };
}

export function isAllowedImageMime(
  value: string,
  limits: Limits = DEFAULT_LIMITS,
): value is AllowedImageMime {
  return (limits.allowedImageMimeTypes as readonly string[]).includes(value);
}
